<template>
  <div class="edit-slot-view">
    <!-- Back header -->
    <div class="view-header">
      <button class="back-btn" @click="$emit('back')">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Edit time slot</h5>
    </div>

    <!-- Loading skeleton -->
    <div class="view-body" v-if="!ready">
      <div class="skeleton-section mb-3">
        <div class="skeleton-row" style="width: 40%; height: 14px; margin-bottom: 16px;"></div>
        <div class="skeleton-form-row">
          <div class="skeleton-row" style="width: 100%; height: 36px;"></div>
          <div class="skeleton-row" style="width: 100%; height: 36px;"></div>
        </div>
      </div>
      <div class="skeleton-section mb-3">
        <div class="skeleton-row" style="width: 30%; height: 14px; margin-bottom: 16px;"></div>
        <div class="skeleton-form-row">
          <div class="skeleton-row" style="width: 100%; height: 36px;"></div>
          <div class="skeleton-row" style="width: 100%; height: 36px;"></div>
        </div>
        <div class="skeleton-form-row mt-2">
          <div class="skeleton-row" style="width: 100%; height: 36px;"></div>
          <div class="skeleton-row" style="width: 100%; height: 36px;"></div>
        </div>
      </div>
    </div>

    <div class="view-body" v-else>
      <!-- Resource info -->
      <div class="resource-info mb-3" v-if="resourceSlot">
        <div class="resource-info__row">
          <span class="resource-info__label">Resource</span>
          <span class="resource-info__value">{{ resourceSlot.resource_name }}</span>
        </div>
        <div class="resource-info__row" v-if="resourceSlot.start_time && resourceSlot.end_time">
          <span class="resource-info__label">Available</span>
          <span class="resource-info__value">{{ formatCoverageTime(resourceSlot.start_time) }} – {{ formatCoverageTime(resourceSlot.end_time) }}</span>
        </div>
      </div>

      <!-- Slot details (event/status + timing — matches CreateSlotDrawer
           which merges these into a single section). -->
      <drawer-section title="Slot details" icon="icon-calendar-60">
        <div class="form-row" v-if="!isStatusLocked">
          <div class="form-col">
            <base-input label="Event" :error="errs.game">
              <el-select
                v-model="selectedGame"
                class="select-primary"
                placeholder="Select event"
                value-key="id"
                filterable
              >
                <el-option
                  v-for="game in games"
                  class="select-primary"
                  :label="game.name"
                  :value="game"
                  :key="'G' + game.id"
                />
              </el-select>
            </base-input>
          </div>
          <div class="form-col">
            <base-input label="Status">
              <el-select
                v-model="selectedStatus"
                class="select-primary"
                placeholder="Status"
              >
                <el-option
                  v-for="(s, i) in allStatuses"
                  class="select-primary"
                  :label="s.label"
                  :value="s.value"
                  :key="'S' + i"
                />
              </el-select>
            </base-input>
          </div>
        </div>

        <!-- All day toggle -->
        <div class="toggle-row mb-3">
          <el-switch
            active-color="#e14eca"
            v-model="slot.is_all_day"
            :disabled="hasCoverageRestriction"
            @change="handleAllDayToggle"
          />
          <label
            class="mb-0 ml-2"
            :class="{ 'toggle-disabled': hasCoverageRestriction }"
            :style="hasCoverageRestriction ? '' : 'cursor: pointer'"
            @click="!hasCoverageRestriction && (slot.is_all_day = !slot.is_all_day)"
          >
            All day event
          </label>
        </div>

        <!-- All day text -->
        <div v-if="slot.is_all_day" class="mb-3">
          <info-hint variant="primary">
            This text will be displayed on the booking slot instead of the time range for all-day events.
          </info-hint>
          <label class="text-white">Display text</label>
          <base-input v-model="slot.all_day_text" placeholder="e.g., Available all day"></base-input>
        </div>

        <!-- Date/time (when not all day) -->
        <template v-if="!slot.is_all_day">
          <div class="form-row">
            <div class="form-col">
              <base-input label="Start date" :error="errs.start_date">
                <el-date-picker
                  :editable="false"
                  type="date"
                  placeholder="Select date"
                  :format="dayOfWeek + dateFormatCal"
                  v-model="slot.start_date"
                  :picker-options="{ firstDayOfWeek: firstDayOfWeek }"
                  @change="onTimingFieldChange"
                />
              </base-input>
            </div>
            <div class="form-col">
              <base-input label="Start time" :error="errs.start_time">
                <el-time-picker
                  v-model="slot.start_time"
                  :format="pickerTimeFormat"
                  placeholder="Select time"
                  @change="onTimingFieldChange"
                />
              </base-input>
            </div>
          </div>
          <div class="form-row">
            <div class="form-col">
              <base-input label="End date" :error="errs.end_date">
                <el-date-picker
                  :editable="false"
                  type="date"
                  placeholder="Select date"
                  :format="dayOfWeek + dateFormatCal"
                  v-model="slot.end_date"
                  :picker-options="{ firstDayOfWeek: firstDayOfWeek }"
                  @change="onTimingFieldChange"
                />
              </base-input>
            </div>
            <div class="form-col">
              <base-input label="End time" :error="errs.end_time">
                <el-time-picker
                  v-model="slot.end_time"
                  :format="pickerTimeFormat"
                  placeholder="Select time"
                  @change="onTimingFieldChange"
                />
              </base-input>
            </div>
          </div>
        </template>
      </drawer-section>

      <!-- Booking options (available/call_to_book only) -->
      <drawer-section
        v-if="!isStatusLocked && ((!isCustomStatus && (selectedStatus === 'available' || selectedStatus === 'call_to_book' || selectedStatus === 'requestable')) || (isCustomStatus && selectedOnlineDisplayMode === 'bookable'))"
        title="Booking options"
        icon="icon-settings"
      >
        <div class="toggle-row mb-3">
          <el-switch active-color="#e14eca" v-model="allow_late_booking" />
          <label class="mb-0 ml-2" style="cursor: pointer" @click="allow_late_booking = !allow_late_booking">
            Allow bookings after start time
          </label>
        </div>

        <!-- Minimum booking notice / max time after start -->
        <info-hint v-if="!allow_late_booking" variant="primary">
          Choose how soon before the event start time a customer can book. This setting will help prevent last minute bookings.
        </info-hint>
        <info-hint v-else variant="primary">
          Allow customers to book up to this amount of time after the event has already started. Useful for events that allow late entry.
        </info-hint>
        <value-period-input
          class="mb-4"
          v-model.number="min_booking_value"
          :period.sync="min_booking_period"
          :periods="min_periods"
          :label="allow_late_booking ? 'Maximum time after booking start' : 'Minimum booking notice'"
          :min="0"
        />

        <info-hint v-if="!allow_late_booking" variant="primary">
          Choose what customers see once this slot enters the minimum notice window: a call to book label, a request to book button, or nothing.
        </info-hint>
        <div class="mb-4" v-if="!allow_late_booking">
          <label>When minimum notice passes</label>
          <base-input class="mb-0">
            <el-select v-model="pastNoticeBehavior" class="select-primary">
              <el-option class="select-primary" label="Show as call to book" :value="0"></el-option>
              <el-option class="select-primary" label="Show as request to book" :value="1"></el-option>
              <el-option class="select-primary" label="Hide slot" :value="2"></el-option>
            </el-select>
          </base-input>
        </div>

        <!-- Maximum bookings in advance -->
        <info-hint variant="primary">
          Choose how far in advance customers can book this event. By default, bookings are generated 12 months in advance.
        </info-hint>
        <value-period-input
          class="mb-2"
          v-model.number="max_booking_value"
          :period.sync="max_booking_period"
          :periods="max_periods"
          label="Maximum bookings in advance"
          :min="0"
        />
      </drawer-section>

      <!-- Display options (available/call_to_book only; custom statuses inherit the booking site's sold out text) -->
      <drawer-section
        v-if="!isStatusLocked && !isCustomStatus && selectedStatus !== 'blocked' && selectedStatus !== 'requestable'"
        title="Display options"
        icon="icon-image-02"
      >
        <info-hint v-if="selectedStatus === 'available' && !allow_late_booking" variant="primary">
          This text will be visible to customers when a slot is blocked off due to minimum booking notice coming into effect.
        </info-hint>
        <info-hint v-else-if="selectedStatus === 'available' && allow_late_booking" variant="primary">
          This text will be visible to customers when a slot is blocked off due to maximum time after start coming into effect.
        </info-hint>
        <info-hint v-else-if="selectedStatus === 'call_to_book'" variant="primary">
          This text will be visible to customers when a slot has the status "Call to book".
        </info-hint>
        <label class="text-white" v-if="selectedStatus === 'available'">
          {{ allow_late_booking ? 'Maximum time after start block off text' : 'Minimum notice block off text' }}
        </label>
        <label class="text-white" v-if="selectedStatus === 'call_to_book'">Time slot text</label>
        <base-input v-model="slot.slot_text"></base-input>

        <div class="toggle-row">
          <el-switch active-color="#e14eca" v-model="slot.has_external_link" />
          <label class="mb-0 ml-2" style="cursor: pointer" @click="slot.has_external_link = !slot.has_external_link">
            Link to external page
          </label>
        </div>
        <div v-if="slot.has_external_link" class="mt-2">
          <base-input v-model="slot.external_link" placeholder="https://example.com"></base-input>
        </div>
      </drawer-section>

      <!-- Custom message: staff annotation shown on the customer-facing slot.
           Hidden for hidden custom statuses, which never reach customers. -->
      <drawer-section
        v-if="!(isCustomStatus && selectedOnlineDisplayMode === 'hidden')"
        title="Custom message"
        icon="icon-chat-33"
      >
        <info-hint variant="primary">
          This text shows in addition to any other text on the customer-facing slot on the booking site.
        </info-hint>
        <div class="toggle-row">
          <el-switch active-color="#e14eca" v-model="slot.custom_message_active" />
          <label class="mb-0 ml-2" style="cursor: pointer" @click="slot.custom_message_active = !slot.custom_message_active">
            Show message on this booking slot
          </label>
        </div>
        <template v-if="slot.custom_message_active">
          <label class="text-white mt-2 d-block">Message</label>
          <base-input
            v-model="slot.custom_message"
            :maxlength="200"
            placeholder="e.g. Closed for Christmas!"
            :error="errs.custom_message"
          ></base-input>
          <small v-if="slot.custom_message_display === 'badge' && slot.custom_message.length > 20" style="color: rgba(255, 255, 255, 0.7); display: block; margin-top: -8px; margin-bottom: 8px;">
            Badges read best with 20 characters or fewer.
          </small>
          <label class="text-white d-block mt-2">Display style</label>
          <segmented-toggle
            v-model="slot.custom_message_display"
            :options="custom_message_display_options"
            size="sm"
          />
        </template>
        <div v-if="slot.custom_message && slot.custom_message_active" class="custom-message-preview mt-3">
          <label class="text-white d-block mb-2">Preview</label>
          <div class="preview-frame">
            <div v-if="slot.custom_message_display === 'text'" class="preview-slot">
              <div class="preview-time">{{ getPreviewTime() }}</div>
              <div class="preview-text">{{ slot.custom_message }}</div>
            </div>
            <div v-else class="preview-slot">
              <span class="preview-badge">{{ slot.custom_message }}</span>
              <div class="preview-time">{{ getPreviewTime() }}</div>
            </div>
          </div>
        </div>
      </drawer-section>

      <!-- Block note (blocked/call_to_book) -->
      <drawer-section
        v-if="!isStatusLocked && selectedStatus !== 'available'"
        title="Notes"
        icon="icon-notes"
      >
        <info-hint variant="primary">
          This note will be visible to staff in the calendar to explain why the slot is blocked.
        </info-hint>
        <label class="text-white">Block note</label>
        <base-input>
          <el-input v-model="slot.block_note" :rows="3" type="textarea" placeholder="Notes..." />
        </base-input>
      </drawer-section>
    </div>

    <!-- Sticky footer -->
    <div class="view-footer">
      <base-button type="secondary" size="sm" @click="$emit('back')">Cancel</base-button>
      <base-button type="primary" size="sm" :disabled="saving" @click="updateSlot">
        <i v-if="saving" class="el-icon-loading mr-1"></i>Save changes
      </base-button>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { API_LOCATION } from '@/api-config';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import swal from 'sweetalert2';
import { pickerDateFormat as toPickerDateFormat, pickerTimeFormat as toPickerTimeFormat } from '@/plugins/dates';
import {
  TimePicker,
  DatePicker,
  Select,
  Option,
  Switch,
  Input
} from 'element-ui';

export default {
  name: 'EditSlotView',

  components: {
    [TimePicker.name]: TimePicker,
    [DatePicker.name]: DatePicker,
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
    [Input.name]: Input
  },

  props: {
    booking: { type: Object, required: true },
    companyId: { type: [String, Number], required: true },
    companyGroupId: { type: [String, Number], required: true },
    dateFormat: { type: String, default: 'MM-DD-YYYY' },
    timeFormat: { type: String, default: 'hh:mm A' },
    firstDayOfWeek: { type: Number, default: 0 },
    showDayOfWeek: { type: Boolean, default: false },
    customStatuses: { type: Array, default: () => [] },
    resourceSlot: { type: Object, default: null }
  },

  data() {
    return {
      ready: false,
      saving: false,
      games: [],
      selectedGame: null,
      selectedStatus: 'available',
      statuses: [
        { label: 'Available', value: 'available' },
        { label: 'Blocked', value: 'blocked' },
        { label: 'Call to book', value: 'call_to_book' },
        { label: 'Requestable', value: 'requestable' }
      ],
      slot: {
        start_date: null,
        start_time: null,
        end_date: null,
        end_time: null,
        block_note: '',
        slot_text: '',
        is_all_day: false,
        all_day_text: '',
        has_external_link: false,
        external_link: '',
        custom_message: '',
        custom_message_display: 'text',
        custom_message_active: false
      },
      custom_message_display_options: [
        { value: 'text', label: 'Text' },
        { value: 'badge', label: 'Badge' }
      ],
      // Booking notice — period lists must match the canonical lists used by
      // Bookings.vue:4764-4769, Calendar.vue:4707-4712, and the schedule
      // editor at Schedules/EditSchedule.vue:2181-2182. min and max use
      // DIFFERENT id-spaces (min: 0-4 covering minutes→months, max: 0-3
      // covering days→years) so they need separate lists.
      allow_late_booking: false,
      min_booking_value: 1,
      min_booking_period: 1,
      hide_past_min_notice: 0,
      request_past_min_notice: 0,
      max_booking_value: 6,
      max_booking_period: 2,
      min_periods: [
        { id: 0, label: 'minute(s)' },
        { id: 1, label: 'hour(s)' },
        { id: 2, label: 'day(s)' },
        { id: 3, label: 'week(s)' },
        { id: 4, label: 'month(s)' }
      ],
      max_periods: [
        { id: 0, label: 'day(s)' },
        { id: 1, label: 'week(s)' },
        { id: 2, label: 'month(s)' },
        { id: 3, label: 'year(s)' }
      ],
      // UI state
      loadedStatusTypeId: null,
      formInitialized: false,
      errs: { game: '', start_date: '', start_time: '', end_date: '', end_time: '' }
    };
  },

  computed: {
    isStatusLocked() {
      const status = this.booking?.status;
      return status === 'booked' || status === 'completed' || status === 'requested';
    },
    isCustomStatus() {
      const builtIn = ['available', 'blocked', 'call_to_book', 'requestable', 'booked', 'completed'];
      return !builtIn.includes(this.selectedStatus);
    },
    // Single control over the two past-notice columns. 0 = call to book
    // (both flags off), 1 = request to book, 2 = hide slot. The columns are
    // mutually exclusive by construction here; requestable wins if data ever
    // holds both.
    pastNoticeBehavior: {
      get() {
        if (Number(this.request_past_min_notice) === 1) return 1;
        if (Number(this.hide_past_min_notice) === 1) return 2;
        return 0;
      },
      set(v) {
        this.request_past_min_notice = v === 1 ? 1 : 0;
        this.hide_past_min_notice = v === 2 ? 1 : 0;
      }
    },
    // The custom-status object for the currently selected status (null for
    // built-in statuses).
    selectedCustomStatus() {
      return this.customStatuses.find(s => s.status_key === this.selectedStatus) || null;
    },
    // Online display mode of the selected custom status; drives the message and
    // booking-option sections. Null for built-in statuses.
    selectedOnlineDisplayMode() {
      return this.selectedCustomStatus ? this.selectedCustomStatus.online_display_mode : null;
    },
    allStatuses() {
      const custom = this.customStatuses
        .filter(s => s.is_active)
        .map(s => ({
          label: s.display_name,
          value: s.status_key
        }));
      const merged = [...this.statuses, ...custom];
      const currentStatus = this.booking?.status;
      if (currentStatus && !merged.some(s => s.value === currentStatus)) {
        merged.push({
          label: currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1).replace(/_/g, ' '),
          value: currentStatus
        });
      }
      return merged;
    },

    // Use the shared helper so every supported group date format —
    // including 'DD MMM YYYY' and 'MMMM DD, YYYY' — maps correctly. The
    // previous hand-rolled converter silently fell back to 'MM/dd/yyyy'
    // for anything outside three numeric formats.
    dateFormatCal() {
      return toPickerDateFormat(this.dateFormat);
    },

    dayOfWeek() {
      return this.showDayOfWeek ? 'ddd, ' : '';
    },

    pickerTimeFormat() {
      return toPickerTimeFormat(this.timeFormat);
    },

    hasCoverageRestriction() {
      if (!this.resourceSlot?.start_time || !this.resourceSlot?.end_time) return false;
      const isFullDay = this.resourceSlot.start_time === '00:00:00' &&
        (this.resourceSlot.end_time === '23:59:59' || this.resourceSlot.end_time === '23:59:00');
      return !isFullDay;
    }
  },

  watch: {
    'slot.start_time'() {
      if (this.formInitialized && !this.slot.is_all_day) this.validateTimingFields();
    },
    'slot.end_time'() {
      if (this.formInitialized && !this.slot.is_all_day) this.validateTimingFields();
    },
    'slot.start_date'() {
      if (this.formInitialized) this.validateTimingFields();
    },
    'slot.end_date'() {
      if (this.formInitialized) this.validateTimingFields();
    }
  },

  async created() {
    await this.loadGames();
    this.populateFromBooking();
    this.ready = true;
  },

  methods: {
    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    getPreviewTime() {
      if (this.slot.is_all_day) return this.slot.all_day_text || 'All day';
      if (!this.slot.start_time) return '12:00 PM';
      const time = moment(this.slot.start_time);
      return time.isValid() ? time.format('h:mm A') : '12:00 PM';
    },

    async loadGames() {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/games`;
        const response = await axios.get(url, config);
        this.games = (response.data || []).filter(g => g.is_enabled !== 0);
      } catch (error) {
        console.error('Failed to load games:', error);
      }
    },

    populateFromBooking() {
      const b = this.booking;
      if (!b) return;

      // Game
      if (b.game_id) {
        this.selectedGame = this.games.find(g => g.id === b.game_id) || null;
      }

      // Status
      this.selectedStatus = b.status || 'available';
      // Preserve the slot's existing custom-status FK so an unchanged status keeps
      // it on save even if customStatuses isn't populated (e.g. a parent that
      // didn't wire the prop) or the status was soft-deleted.
      this.loadedStatusTypeId = b.status_type_id != null ? b.status_type_id : null;

      // Dates
      if (b.booking_date) this.slot.start_date = moment(String(b.booking_date).substring(0, 10), 'YYYY-MM-DD').toDate();
      if (b.end_date) {
        this.slot.end_date = moment(String(b.end_date).substring(0, 10), 'YYYY-MM-DD').toDate();
      } else if (b.booking_date) {
        this.slot.end_date = moment(String(b.booking_date).substring(0, 10), 'YYYY-MM-DD').toDate();
      }

      // Times
      if (b.start_time) {
        const parts = b.start_time.split(':');
        const time = new Date();
        time.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
        this.slot.start_time = time;
      }
      if (b.end_time) {
        const parts = b.end_time.split(':');
        const time = new Date();
        time.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
        this.slot.end_time = time;
      }

      // All day
      this.slot.is_all_day = Boolean(b.is_all_day);
      this.slot.all_day_text = b.all_day_text || '';

      // Other fields
      this.slot.slot_text = b.slot_text || '';
      this.slot.has_external_link = Boolean(b.has_external_link);
      this.slot.external_link = b.external_link || '';

      // Custom message. Load-time clean-up of the legacy trap state (active=1 +
      // blank message, from the old DB default): force the toggle off so the
      // user isn't blocked by validation when they go to save unrelated edits.
      // After the drawer is open, leave the toggle alone — user controls it.
      this.slot.custom_message = b.custom_message || '';
      this.slot.custom_message_display = b.custom_message_display || 'text';
      this.slot.custom_message_active = this.slot.custom_message.trim()
        ? !!b.custom_message_active
        : false;

      // Booking notice — use != null so a stored id of 0 (minutes for min,
      // days for max) doesn't fall through to the default. Mirrors legacy
      // Bookings.vue:10070 / Calendar.vue's setBookingInfo lookup.
      this.allow_late_booking = Boolean(b.allow_late_booking);
      // Use != null so a stored value of 0 (e.g. "0 minutes minimum notice")
      // round-trips instead of being replaced by the default on every load.
      this.min_booking_value = b.min_booking_value != null ? b.min_booking_value : 1;
      this.max_booking_value = b.max_booking_value != null ? b.max_booking_value : 6;
      // Period storage is just the id (matches ValuePeriodInput contract).
      this.min_booking_period = b.min_booking_period != null ? b.min_booking_period : 1;
      this.max_booking_period = b.max_booking_period != null ? b.max_booking_period : 2;
      // Hide-past-min-notice: tinyint 0/1; default 0 (show as call-to-book).
      this.hide_past_min_notice = b.hide_past_min_notice != null ? b.hide_past_min_notice : 0;
      this.request_past_min_notice = b.request_past_min_notice != null ? b.request_past_min_notice : 0;

      this.$nextTick(() => {
        this.formInitialized = true;
        this.validateTimingFields();
      });
    },

    handleAllDayToggle() {
      if (this.slot.is_all_day) {
        const startDate = new Date(this.slot.start_date);
        startDate.setHours(0, 0, 0, 0);
        this.slot.start_time = startDate;
        const endDate = new Date(this.slot.start_date);
        endDate.setHours(23, 59, 0, 0);
        this.slot.end_time = endDate;
        this.slot.end_date = new Date(this.slot.start_date);
        this.errs.start_time = '';
        this.errs.end_time = '';
      } else {
        this.$nextTick(() => this.validateTimingFields());
      }
    },

    onTimingFieldChange() {
      if (this.formInitialized) {
        this.$nextTick(() => this.validateTimingFields());
      }
    },

    validateTimingFields() {
      this.errs.start_date = '';
      this.errs.end_date = '';
      this.errs.start_time = '';
      this.errs.end_time = '';

      if (this.slot.is_all_day) return;

      const timeFormatDisplay = this.timeFormat === 'HH:mm' ? 'HH:mm' : 'h:mm A';

      // Time validation against coverage window
      if (this.resourceSlot?.start_time && this.resourceSlot?.end_time) {
        const coverageStart = moment(this.resourceSlot.start_time, 'HH:mm:ss');
        const coverageEnd = moment(this.resourceSlot.end_time, 'HH:mm:ss');

        if (this.slot.start_time) {
          const slotStartTime = moment(moment(this.slot.start_time).format('HH:mm:ss'), 'HH:mm:ss');
          if (slotStartTime.isBefore(coverageStart)) {
            this.errs.start_time = `Must be after ${coverageStart.format(timeFormatDisplay)}`;
          }
        }
        if (this.slot.end_time) {
          const slotEndTime = moment(moment(this.slot.end_time).format('HH:mm:ss'), 'HH:mm:ss');
          if (slotEndTime.isAfter(coverageEnd)) {
            this.errs.end_time = `Must be before ${coverageEnd.format(timeFormatDisplay)}`;
          }
        }
      }

      // Date validation against coverage window
      if (this.resourceSlot?.start_date && this.resourceSlot?.end_date) {
        const coverageStartDate = moment(this.resourceSlot.start_date).startOf('day');
        const coverageEndDate = moment(this.resourceSlot.end_date).startOf('day');
        if (this.slot.start_date && moment(this.slot.start_date).startOf('day').isBefore(coverageStartDate)) {
          this.errs.start_date = `Must be on or after ${coverageStartDate.format(this.dateFormat)}`;
        }
        if (this.slot.end_date && moment(this.slot.end_date).startOf('day').isAfter(coverageEndDate)) {
          this.errs.end_date = `Must be on or before ${coverageEndDate.format(this.dateFormat)}`;
        }
      }

      // Time order (same day only)
      if (this.slot.start_time && this.slot.end_time && this.slot.start_date && this.slot.end_date) {
        const startDate = moment(this.slot.start_date).startOf('day');
        const endDate = moment(this.slot.end_date).startOf('day');
        if (startDate.isSame(endDate)) {
          const slotStartTime = moment(moment(this.slot.start_time).format('HH:mm:ss'), 'HH:mm:ss');
          const slotEndTime = moment(moment(this.slot.end_time).format('HH:mm:ss'), 'HH:mm:ss');
          if (slotStartTime.isSameOrAfter(slotEndTime) && !this.errs.end_time) {
            this.errs.end_time = 'End time must be after start time';
          }
        }
      }
    },

    validateForm() {
      let isValid = true;
      this.errs = { game: '', start_date: '', start_time: '', end_date: '', end_time: '', custom_message: '' };

      if (!this.selectedGame) { this.errs.game = 'Please select a game'; isValid = false; }
      if (!this.slot.start_date) { this.errs.start_date = 'Start date is required'; isValid = false; }
      if (!this.slot.start_time && !this.slot.is_all_day) { this.errs.start_time = 'Start time is required'; isValid = false; }
      if (!this.slot.end_date) { this.errs.end_date = 'End date is required'; isValid = false; }
      if (!this.slot.end_time && !this.slot.is_all_day) { this.errs.end_time = 'End time is required'; isValid = false; }

      if (this.slot.custom_message_active && (!this.slot.custom_message || !this.slot.custom_message.trim())) {
        this.errs.custom_message = 'Add a message before enabling, or turn the toggle off';
        this.$notify({ type: 'warning', message: 'Add a message before enabling', timeout: 3000 });
        isValid = false;
      }

      // Date order
      if (this.slot.start_date && this.slot.end_date) {
        if (moment(this.slot.end_date).startOf('day').isBefore(moment(this.slot.start_date).startOf('day'))) {
          this.errs.end_date = 'End date must be on or after start date';
          isValid = false;
        }
      }

      const timeFormatDisplay = this.timeFormat === 'HH:mm' ? 'HH:mm' : 'h:mm A';

      // Coverage validation
      if (this.resourceSlot?.start_time && this.resourceSlot?.end_time) {
        const coverageStart = moment(this.resourceSlot.start_time, 'HH:mm:ss');
        const coverageEnd = moment(this.resourceSlot.end_time, 'HH:mm:ss');

        if (this.slot.is_all_day && this.hasCoverageRestriction) {
          this.errs.start_time = `All day events are not allowed. Resource hours: ${coverageStart.format(timeFormatDisplay)} - ${coverageEnd.format(timeFormatDisplay)}`;
          isValid = false;
        }

        if (!this.slot.is_all_day && this.slot.start_time && this.slot.end_time) {
          const slotStartTime = moment(moment(this.slot.start_time).format('HH:mm:ss'), 'HH:mm:ss');
          const slotEndTime = moment(moment(this.slot.end_time).format('HH:mm:ss'), 'HH:mm:ss');
          if (slotStartTime.isBefore(coverageStart)) { this.errs.start_time = `Must be after ${coverageStart.format(timeFormatDisplay)}`; isValid = false; }
          if (slotEndTime.isAfter(coverageEnd)) { this.errs.end_time = `Must be before ${coverageEnd.format(timeFormatDisplay)}`; isValid = false; }
        }
      }

      // Time order (same day)
      if (!this.slot.is_all_day && this.slot.start_time && this.slot.end_time && this.slot.start_date && this.slot.end_date) {
        if (moment(this.slot.start_date).startOf('day').isSame(moment(this.slot.end_date).startOf('day'))) {
          const slotStartTime = moment(moment(this.slot.start_time).format('HH:mm:ss'), 'HH:mm:ss');
          const slotEndTime = moment(moment(this.slot.end_time).format('HH:mm:ss'), 'HH:mm:ss');
          if (slotStartTime.isSameOrAfter(slotEndTime) && !this.errs.end_time) {
            this.errs.end_time = 'End time must be after start time';
            isValid = false;
          }
        }
      }

      return isValid;
    },

    async updateSlot() {
      if (!this.validateForm()) return;

      this.saving = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings/${this.booking.id}/v2`;

        const data = {
          game_id: this.selectedGame.id,
          booking_date: moment(this.slot.start_date).format('YYYY-MM-DD'),
          end_date: moment(this.slot.end_date).format('YYYY-MM-DD'),
          status: this.selectedStatus,
          // Persist the custom-status FK so customer-side joins resolve the online
          // display config. The lambda always writes status_type_id when status is
          // present, so we must send the right value: the active custom status's id
          // when resolvable, else (for an unchanged/soft-deleted custom status) the
          // FK we loaded, else null for built-in statuses.
          status_type_id: this.selectedCustomStatus
            ? this.selectedCustomStatus.id
            : (this.isCustomStatus ? this.loadedStatusTypeId : null),
          start_time: this.slot.is_all_day ? '00:00:00' : moment(this.slot.start_time).format('HH:mm:ss'),
          end_time: this.slot.is_all_day ? '23:59:59' : moment(this.slot.end_time).format('HH:mm:ss'),
          is_all_day: this.slot.is_all_day,
          all_day_text: this.slot.all_day_text || '',
          slot_text: this.slot.slot_text || '',
          block_note: this.slot.block_note || '',
          custom_message: this.slot.custom_message || '',
          custom_message_display: this.slot.custom_message_display || 'text',
          custom_message_active: this.slot.custom_message_active ? 1 : 0,
          has_external_link: this.slot.has_external_link,
          external_link: this.slot.external_link || '',
          allow_late_booking: this.allow_late_booking,
          min_booking_value: this.min_booking_value,
          min_booking_period: this.min_booking_period,
          hide_past_min_notice: this.hide_past_min_notice,
          request_past_min_notice: this.request_past_min_notice,
          max_booking_value: this.max_booking_value,
          max_booking_period: this.max_booking_period
        };

        await axios.put(url, data, config);

        this.$notify({
          type: 'success',
          message: 'Time slot has been updated',
          icon: 'tim-icons icon-check-2'
        });
        this.$emit('updated');
      } catch (error) {
        console.error('Failed to update slot:', error);
        if (error.response?.status === 401) {
          this.$router.push('/forbidden');
        } else {
          swal('Error', error.response?.data || 'Failed to update slot', 'error');
        }
      } finally {
        this.saving = false;
      }
    },

    formatCoverageTime(timeString) {
      return moment(timeString, 'HH:mm:ss').format(this.timeFormat === 'HH:mm' ? 'HH:mm' : 'h:mm A');
    }
  }
};
</script>

<style lang="scss" scoped>
.edit-slot-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.view-header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  gap: 12px;
}

.back-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
}

.view-title {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.view-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.view-footer {
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.form-row {
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
}

.form-col {
  flex: 1;
}

.toggle-row {
  display: flex;
  align-items: center;

  label {
    color: #fff;
    font-size: 13px;
  }
}

.toggle-disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
}


.skeleton-section {
  margin-bottom: 16px;
}

.skeleton-row {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-form-row {
  display: flex;
  gap: 12px;
}

@keyframes skeleton-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.resource-info {
  background: rgba(225, 78, 202, 0.05);
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 6px;
  padding: 10px 14px;

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;

    & + & {
      margin-top: 6px;
    }
  }

  &__label {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    min-width: 70px;
  }

  &__value {
    font-size: 13px;
    color: #fff;
  }
}

.custom-message-preview {
  background: rgba(30, 30, 47, 0.6);
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 0.4285rem;
  padding: 12px;
}
.preview-frame {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}
.preview-slot {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: 1px solid #e14eca;
  border-radius: 0.4285rem;
  color: #e14eca;
  background: transparent;
  min-width: 140px;
  text-align: center;
}
.preview-time {
  font-size: 14px;
  font-weight: 600;
}
.preview-text {
  font-size: 11px;
  margin-top: 4px;
  word-break: normal;
  overflow-wrap: break-word;
}
.preview-badge {
  position: absolute;
  top: -10px;
  right: -8px;
  background: #e14eca;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>

<style lang="scss">
// Light mode overrides
.white-content {
  .edit-slot-view {
    .view-header {
      border-bottom-color: #e9ecef;
    }

    .view-title {
      color: #344675;
    }

    .back-btn {
      background: #f0f2f5;
      border-color: #d1d5db;
      color: #525f7f;

      &:hover {
        background: #e2e6ea;
        color: #344675;
      }
    }

    .view-footer {
      border-top-color: #e9ecef;
    }

    .toggle-row label {
      color: #525f7f;
    }

    .resource-info {
      background: rgba(204, 95, 173, 0.08);
      border-color: rgba(204, 95, 173, 0.3);

      &__label { color: #9A9A9A; }
      &__value { color: #344675; }
    }

    // Standalone labels with text-white (not inside .toggle-row)
    label.text-white {
      color: #525f7f !important;
    }

    .text-white {
      color: #344675 !important;
    }

    // Custom message preview — dark frame washes out on white card; soft on white instead
    .custom-message-preview {
      background: #f8f9fa;
      border-color: rgba(0, 0, 0, 0.15);
    }
    .preview-slot {
      background: #ffffff;
    }
  }
}
</style>
